import React, { useLayoutEffect, useState } from 'react';
import { Alert, Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row } from 'reactstrap';
import { useForm } from '@mantine/form';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { LoadingOverlay } from '@mantine/core';
import { AppPaths } from '@/configs/appPaths';
import logo from '@/assets/images/logo.svg';
import { useMutation } from '@tanstack/react-query';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import { isAppError } from '@/utils/utilities';
import { getErrorMessage } from '@/types/ErrorCodes';

export enum SubmitStatus {
	THROTTLE,
	LOADING,
	ERROR,
	SUCCESS,
}

export interface LoginValues {
	email: string;
	password: string;
}

const initialValues: LoginValues = {
	email: '',
	password: '',
};

export const Login: React.FC = () => {
	const { login } = useAuth();

	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [resultMessage, setResultMessage] = useState('');
	const [searchParams, setSearchParams] = useSearchParams();

	const form = useForm({
		initialValues,
	});

	const handleSubmit = async (loginValues: LoginValues) => {
		setStatus(SubmitStatus.LOADING);
		setResultMessage('');

		try {
			const result = await login(loginValues);

			if (result === SubmitStatus.ERROR) {
				setStatus(SubmitStatus.ERROR);
				setResultMessage(
					'Podane dane są nieprawidłowe. Sprawdź poprawność wprowadzonych danych'
				);
				return;
			}

			setStatus(SubmitStatus.SUCCESS);
			form.reset();
		} catch (error) {
			console.error('Login error:', error);
			setStatus(SubmitStatus.ERROR);
			setResultMessage('Wystąpił błąd podczas logowania. Spróbuj ponownie.');
		}
	};

	const verifyRegistrationMutation = useMutation({
		mutationFn: UnauthenticatedApi.confirmAccount,
		onSuccess: () => {
			setSearchParams({});
			setStatus(SubmitStatus.SUCCESS);
			setResultMessage('Poprawnie aktywowano konto. Możesz się zalogować.');
		},
		onError: (error) => {
			const isError = isAppError(error);
			if (!isError) return;

			setStatus(SubmitStatus.ERROR);
			setResultMessage(getErrorMessage(error.stack));
		},
	});

	useLayoutEffect(() => {
		const hash = searchParams.get('token');
		if (!hash) return;

		verifyRegistrationMutation.mutate(hash);
	}, []);

	return (
		<div className="account-pages">
			<Container className="position-absolute">
				<Row className="justify-content-center">
					<Col md={8} lg={6} xl={5}>
						<Card className="overflow-hidden mb-0">
							<LoadingOverlay visible={status === SubmitStatus.LOADING} />
							<div className="logo-bar">
								<img alt="logo" src={logo} />
							</div>
							<CardBody>
								<div className="p-2">
									<CardTitle className="mb-3">Zaloguj się do panelu</CardTitle>
									<Form
										className="form-horizontal"
										onSubmit={form.onSubmit(handleSubmit)}
									>
										{status === SubmitStatus.ERROR && (
											<Alert color="danger">{resultMessage}</Alert>
										)}
										{status === SubmitStatus.SUCCESS && (
											<Alert color="success">{resultMessage}</Alert>
										)}
										<div className="mb-3">
											<Label className="form-label">Email</Label>
											<Input
												{...form.getInputProps('email')}
												required
												type="email"
												className="form-control"
												placeholder="Wpisz adres email"
												invalid={status === SubmitStatus.ERROR}
											/>
										</div>
										<div className="mb-4">
											<Label className="form-label">Hasło</Label>
											<Input
												{...form.getInputProps('password')}
												required
												type="password"
												className="form-control"
												placeholder="Wpisz hasło"
												invalid={status === SubmitStatus.ERROR}
											/>
										</div>
										<div className="mt-3 d-grid">
											<Button color="primary" type="submit">
												Zaloguj się
											</Button>
										</div>
										<div className="mt-3 text-center">
											<Link
												to={`/${AppPaths.RESET_PASSWORD}`}
												className="text-muted"
											>
												<i className="mdi mdi-lock me-1" />
												Nie pamiętam hasła
											</Link>
										</div>
										<div className="mt-3 text-center">
											<Link to={`/${AppPaths.REGISTER}`} className="text-muted">
												<i className="mdi mdi-lock me-1" />
												Rejestracja
											</Link>
										</div>
									</Form>
								</div>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</div>
	);
};
